
import { Ball, SimulationConfig, MorphData, Vector2D } from '../../types';
import { ShapeUtils } from './ShapeUtils';

export const resolveOutsideColliderCollision = (
  ball: Ball,
  centerX: number,
  centerY: number,
  config: SimulationConfig
): number => {
  if (!config.outsideColliderEnabled) return 0;
  
  const halfWidth = (config.outsideColliderWidth * config.outsideColliderScale) / 2;
  const halfHeight = (config.outsideColliderHeight * config.outsideColliderScale) / 2;
  const minX = (config.outsideColliderOffsetX || 0) - halfWidth;
  const maxX = (config.outsideColliderOffsetX || 0) + halfWidth;
  const minY = (config.outsideColliderOffsetY || 0) - halfHeight;
  const maxY = (config.outsideColliderOffsetY || 0) + halfHeight;
  
  let impact = 0;
  const ballRadius = ball.radius;
  
  // X boundary
  if (ball.pos.x - ballRadius < minX) {
      ball.pos.x = minX + ballRadius;
      ball.vel.x *= -config.outsideColliderRestitution;
      impact = Math.abs(ball.vel.x);
  } else if (ball.pos.x + ballRadius > maxX) {
      ball.pos.x = maxX - ballRadius;
      ball.vel.x *= -config.outsideColliderRestitution;
      impact = Math.abs(ball.vel.x);
  }
  
  // Y boundary
  if (ball.pos.y - ballRadius < minY) {
      ball.pos.y = minY + ballRadius;
      ball.vel.y *= -config.outsideColliderRestitution;
      impact = Math.max(impact, Math.abs(ball.vel.y));
  } else if (ball.pos.y + ballRadius > maxY) {
      ball.pos.y = maxY - ballRadius;
      ball.vel.y *= -config.outsideColliderRestitution;
      impact = Math.max(impact, Math.abs(ball.vel.y));
  }
  
  return impact;
};

export const isInsideCollider = (
  centerX: number,
  centerY: number,
  radius: number,
  config: SimulationConfig,
  margin: number = 0
): boolean => {
  if (!config.outsideColliderEnabled) return true;
  
  const halfWidth = (config.outsideColliderWidth * config.outsideColliderScale) / 2 + margin;
  const halfHeight = (config.outsideColliderHeight * config.outsideColliderScale) / 2 + margin;
  const minX = (config.outsideColliderOffsetX || 0) - halfWidth;
  const maxX = (config.outsideColliderOffsetX || 0) + halfWidth;
  const minY = (config.outsideColliderOffsetY || 0) - halfHeight;
  const maxY = (config.outsideColliderOffsetY || 0) + halfHeight;
  
  return (centerX - radius >= minX && centerX + radius <= maxX && centerY - radius >= minY && centerY + radius <= maxY);
};

export const resolveWallCollision = (
  ball: Ball, 
  centerX: number, 
  centerY: number, 
  radius: number, 
  config: SimulationConfig,
  morph?: MorphData,
  isEscapeRing: boolean = false,
  onCollision?: (pos: Vector2D, radius: number, color: string) => void
): number => {
  const relX = ball.pos.x;
  const relY = ball.pos.y;
  const dist = Math.sqrt(relX * relX + relY * relY);
  
  const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
  const targetShape = morph ? morph.to : shape;
  const t = morph ? morph.factor : 0;
  const rotRad = (config.containerRotation || 0) * Math.PI / 180;
  
  const angle = Math.atan2(relY, relX);
  const lineWidth = isEscapeRing ? (config.escapeRingThickness || 10) : (config.containerLineWidth || 4);
  const boundaryRadius = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, shape, targetShape, t, radius, morph?.baseRadius) - lineWidth / 2;
  
  if (dist + ball.radius > boundaryRadius) {
      const localX = relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad);
      const localY = relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad);
      
      const localNormal = ShapeUtils.getNormal({x: localX, y: localY}, shape, radius, targetShape, t, morph?.baseRadius);
      
      const normal = { x: 0, y: 0 };
      normal.x = localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad);
      normal.y = localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad);

      const radX = dist > 0 ? relX / dist : 1;
      const radY = dist > 0 ? relY / dist : 0;
      const dot = normal.x * radX + normal.y * radY;
      
      const radialDiff = dist - boundaryRadius;
      const trueOverlap = (radialDiff * dot) + ball.radius;
      
      const radialMargin = radius * 0.8; 
      const isLargeBall = ball.radius > radius * 0.5;
      
      // Only resolve if moving towards the wall and overlapping
      const velDot = ball.vel.x * normal.x + ball.vel.y * normal.y;

      if (trueOverlap > 0 && velDot > 0 && (dist > boundaryRadius - radialMargin || isLargeBall)) {
          // Limit correction to prevent teleportation
          const maxCorrection = 10;
          const correction = Math.min(trueOverlap + 0.1, maxCorrection);
          ball.pos.x -= normal.x * correction;
          ball.pos.y -= normal.y * correction;
          
          const bounce = calculateBounce(ball, normal.x, normal.y, config);
          
          if (onCollision) {
              const contactPoint = {
                  x: ball.pos.x + normal.x * ball.radius,
                  y: ball.pos.y + normal.y * ball.radius
              };
              onCollision(contactPoint, ball.radius, ball.color);
          }
          
          return bounce;
      }
  }
  
  return 0;
};

export const calculateBounce = (ball: Ball, nx: number, ny: number, config: SimulationConfig, overrideRestitution?: number): number => {
    const dot = ball.vel.x * nx + ball.vel.y * ny;
    
    if (dot > 0) { 
      const restitution = overrideRestitution ?? (config.mode === 'SPACE_SPIRAL' 
        ? (ball.isBox ? (config.spaceSpiralBoxRestitution ?? 1.0) : (config.spaceSpiralRestitution ?? 0.5))
        : config.restitution);

      const vnx = dot * nx;
      const vny = dot * ny;
      const vtx = ball.vel.x - vnx;
      const vty = ball.vel.y - vny;

      const MIN_BOUNCE_VELOCITY = restitution > 0.1 ? 2.5 : 0; 
      let bounceSpeed = dot * restitution;
      if (restitution > 0.1 && bounceSpeed < MIN_BOUNCE_VELOCITY) bounceSpeed = MIN_BOUNCE_VELOCITY;

      ball.vel.x = vtx - bounceSpeed * nx;
      ball.vel.y = vty - bounceSpeed * ny;

      let speed = Math.sqrt(ball.vel.x ** 2 + ball.vel.y ** 2);
      
      if (restitution > 1) speed += (restitution - 1) * 0.8; 

      const isAtBottom = ny > 0.8; 
      const MIN_HORIZONTAL = (config.mode === 'SPACE_SPIRAL' || restitution < 0.1) ? 0 : (2.5 * config.antiStallMultiplier);
      if (config.mode !== 'SPACE_SPIRAL' && restitution >= 0.1 && isAtBottom && Math.abs(ball.vel.x) < MIN_HORIZONTAL) {
        ball.vel.x = (ball.vel.x >= 0 ? 1 : -1) * (MIN_HORIZONTAL + Math.random() * 2);
      }

      const MAX_SPEED = config.maxVelocityLimit;
      const MIN_SPEED = (config.mode === 'SPACE_SPIRAL' || restitution < 0.1) ? 0 : config.minVelocityLimit; 
      
      if (config.mode !== 'SPACE_SPIRAL') {
          if (speed > MAX_SPEED) speed = MAX_SPEED;
          else if (speed < MIN_SPEED && speed > 0.01) speed = MIN_SPEED;
      }

      const currentSpeed = Math.sqrt(ball.vel.x ** 2 + ball.vel.y ** 2);
      if (currentSpeed > 0) {
        ball.vel.x = (ball.vel.x / currentSpeed) * speed;
        ball.vel.y = (ball.vel.y / currentSpeed) * speed;
      } else if (restitution < 0.01 && Math.sqrt(vtx**2 + vty**2) < 0.1) {
        ball.vel.x = 0;
        ball.vel.y = 0;
      }
      return dot;
    }
    return 0;
};
